import React from 'react';
import Spacing from '../../Spacing';
import { pageTitle } from '../../../helpers/PageTitle';
import { Link } from 'react-router-dom';
import authService from '../../../services/authService';
import { AvatarDisplay } from '../../AvatarSelector';
import { DEFAULT_AVATAR } from '../../../utils/avatars';
import { useAuth } from '../../../context/AuthContext';
import likeService from '../../../services/likeService';
import { FaHeart } from 'react-icons/fa';

// Animation styles
const animations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .animate-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
  }

  .animate-scale-in {
    animation: scaleIn 0.5s ease-out forwards;
  }

  .stagger-1 {
    animation-delay: 0.1s;
    opacity: 0;
  }

  .stagger-2 {
    animation-delay: 0.2s;
    opacity: 0;
  }

  .stagger-3 {
    animation-delay: 0.3s;
    opacity: 0;
  }

  .stagger-4 {
    animation-delay: 0.4s;
    opacity: 0;
  }
`;

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [userPosts, setUserPosts] = React.useState([]);
  const [likedPosts, setLikedPosts] = React.useState([]);
  const [bannerImage, setBannerImage] = React.useState(null);
  const [uploadingBanner, setUploadingBanner] = React.useState(false);

  // Inject animations
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = animations;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  React.useEffect(() => {
    loadUserData();
    // Debug user data
    console.log('User data:', user);
    console.log('User date_created:', user?.date_created);

    // Load banner from user profile if available
    if (user?.banner_image) {
      const bannerUrl = `http://localhost/cms/assets/${user.banner_image}`;
      console.log('Loading banner from user profile:', bannerUrl);
      setBannerImage(bannerUrl);
    } else {
      console.log('No banner_image found in user profile');
      setBannerImage(null);
    }
  }, [user]);

  async function loadUserData() {
    try {
      // Load user's posts
      const token = authService.getAccessToken();
      const response = await fetch(
        `http://localhost/cms/items/posts?filter[user_created][_eq]=${user?.id}&fields=id,title,status,date_created,featured_image`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserPosts(data.data || []);
      }

      // Load user's liked posts
      if (user?.id) {
        try {
          const likedPostsData = await likeService.getUserLikedPosts(user.id);
          setLikedPosts(likedPostsData);
        } catch (error) {
          console.error('Failed to load liked posts:', error);
          setLikedPosts([]);
        }
      }
    } catch (error) {
      console.error('Failed to load user posts:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleBannerUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB for performance)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB for optimal performance');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploadingBanner(true);

    try {
      let token = authService.getAccessToken();

      // Upload image to Directus
      const formData = new FormData();
      formData.append('file', file);

      let uploadResponse = await fetch('http://localhost/cms/files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      // If permission error, try refreshing token and retry once
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        console.error('Upload error details:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          errorData,
          currentToken: token?.substring(0, 20) + '...' // Log partial token for debugging
        });

        const errorMessage = errorData.errors?.[0]?.message || '';

        // Check if it's a permission error (403) or token error (401)
        if (uploadResponse.status === 403 || uploadResponse.status === 401 || errorMessage.includes('permission')) {
          console.log('Permission/Auth error detected. Status:', uploadResponse.status);
          console.log('Attempting token refresh...');

          try {
            // Refresh the token
            const newToken = await authService.forceRefresh();
            console.log('Token refreshed successfully');
            console.log('New token (partial):', newToken?.substring(0, 20) + '...');

            // Retry upload with new token - IMPORTANT: recreate FormData
            const retryFormData = new FormData();
            retryFormData.append('file', file);

            uploadResponse = await fetch('http://localhost/cms/files', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${newToken}`,
              },
              body: retryFormData,
            });

            if (!uploadResponse.ok) {
              const retryError = await uploadResponse.json();
              console.error('Retry failed:', retryError);
              throw new Error(retryError.errors?.[0]?.message || 'Failed to upload banner image after token refresh');
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            throw new Error(`Token refresh failed: ${refreshError.message}. Please log out and log back in.`);
          }
        } else {
          throw new Error(errorMessage || 'Failed to upload banner image');
        }
      }

      const uploadData = await uploadResponse.json();
      const fileId = uploadData.data.id;

      // Save banner to user profile
      const updateUserResponse = await fetch(`http://localhost/cms/users/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authService.getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          banner_image: fileId,
        }),
      });

      if (!updateUserResponse.ok) {
        const updateError = await updateUserResponse.json();
        console.error('Failed to update user profile:', updateError);
        throw new Error('Banner uploaded but failed to save to profile. Please try again.');
      }

      // Update local banner display
      setBannerImage(`http://localhost/cms/assets/${fileId}`);

      // Refresh user data to get updated banner_image field
      await refreshUser();

      alert('Banner image updated successfully!');
    } catch (error) {
      console.error('Banner upload failed:', error);
      alert(`Failed to upload banner image: ${error.message}`);
    } finally {
      setUploadingBanner(false);
    }
  }

  pageTitle('Dashboard');

  if (loading) {
    return (
      <>
        <Spacing lg="140" md="80" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <h2>Loading...</h2>
        </div>
        <Spacing lg="140" md="80" />
      </>
    );
  }

  return (
    <>
      <Spacing lg="140" md="80" />
      <Spacing lg="75" md="60" />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
        {/* Profile Banner */}
        <div className="animate-fade-in-up" style={{
          position: 'relative',
          backgroundColor: 'rgba(79, 71, 71, 0.1)',
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          {/* Banner Background */}
          <div style={{
            position: 'relative',
            height: '200px',
            ...(bannerImage ? {
              backgroundImage: `url("${bannerImage}")`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            } : {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }),
          }}>
            <label
              htmlFor="banner-upload"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '6px',
                cursor: uploadingBanner ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#5625d8',
              }}
            >
              {uploadingBanner ? 'Uploading...' : 'Change Banner'}
            </label>
            <input
              id="banner-upload"
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              disabled={uploadingBanner}
              style={{ display: 'none' }}
            />
          </div>

          {/* Profile Info */}
          <div style={{
            padding: '2rem',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '1.5rem',
          }}>
            <div style={{ marginTop: '-80px', flexShrink: 0 }}>
              <AvatarDisplay
                avatarId={user?.description || DEFAULT_AVATAR.id}
                size="xlarge"
              />
            </div>
            <div style={{ flex: 1, paddingBottom: '0.5rem' }}>
              <h1 className="cs_section_title cs_fs_32 mb-0">
                {user?.first_name} {user?.last_name}
              </h1>
              <p style={{ marginTop: '0.5rem', opacity: 0.7, fontSize: '16px' }}>
                {user?.email}
              </p>
              <p style={{ marginTop: '0.25rem', opacity: 0.6, fontSize: '14px' }}>
                Member since {(() => {
                  // Use date_created if available, otherwise use January 2025 as default
                  const memberDate = user?.date_created ? new Date(user.date_created) : new Date('2025-01-01');
                  return memberDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* My Articles Section */}
        <div className="animate-fade-in-up stagger-1" style={{ marginTop: '3rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h2 className="cs_section_title cs_fs_32 mb-0">My Articles</h2>
            <Link
              to="/blog"
              className="cs_btn cs_style_1"
              style={{ padding: '0.5rem 1rem', fontSize: '14px' }}
            >
              View All Posts
            </Link>
          </div>

          {userPosts.length === 0 ? (
            <div style={{
              backgroundColor: 'rgba(79, 71, 71, 0.1)',
              padding: '3rem',
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <p style={{ fontSize: '18px', opacity: 0.7, marginBottom: '1rem' }}>
                You haven't created any articles yet.
              </p>
              <Link to="/blog" className="cs_btn cs_style_1">
                Explore Blog
              </Link>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem',
            }}>
              {userPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  className={`animate-scale-in stagger-${Math.min(index + 1, 4)}`}
                >
                  <div style={{
                    backgroundColor: 'rgba(79, 71, 71, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    {post.featured_image && (
                      <div style={{
                        height: '180px',
                        background: `url(http://localhost/cms/assets/${post.featured_image}) center/cover`,
                      }} />
                    )}
                    <div style={{ padding: '1.5rem' }}>
                      <h3 className="cs_fs_18" style={{ marginBottom: '0.5rem' }}>
                        {post.title}
                      </h3>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '1rem',
                        fontSize: '14px',
                        opacity: 0.7,
                      }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          backgroundColor: post.status === 'published'
                            ? 'rgba(40, 167, 69, 0.2)'
                            : 'rgba(255, 193, 7, 0.2)',
                          color: post.status === 'published' ? '#28a745' : '#ffc107',
                          fontWeight: 'bold',
                        }}>
                          {post.status}
                        </span>
                        <span>
                          {new Date(post.date_created).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* My Favorite Posts Section */}
        <div className="animate-fade-in-up stagger-2" style={{ marginTop: '3rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h2 className="cs_section_title cs_fs_32 mb-0">
              <FaHeart style={{ color: '#ff4458', marginRight: '0.5rem', verticalAlign: 'middle' }} />
              My Favorite Posts
            </h2>
          </div>

          {likedPosts.length === 0 ? (
            <div style={{
              backgroundColor: 'rgba(79, 71, 71, 0.1)',
              padding: '3rem 2rem',
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <FaHeart style={{ fontSize: '3rem', color: '#ff4458', opacity: 0.3, marginBottom: '1rem' }} />
              <p style={{ opacity: 0.7, marginBottom: '1.5rem' }}>
                You haven't liked any posts yet. Start exploring our blog and save your favorites!
              </p>
              <Link to="/blog" className="cs_btn cs_style_1">
                Explore Blog
              </Link>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem',
            }}>
              {likedPosts.map((item, index) => {
                const post = item.post;
                if (!post) return null;

                return (
                  <Link
                    key={item.id}
                    to={`/blog/${post.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    className={`animate-scale-in stagger-${Math.min(index + 1, 4)}`}
                  >
                    <div style={{
                      backgroundColor: 'rgba(79, 71, 71, 0.1)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      border: '2px solid rgba(255, 68, 88, 0.2)',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 68, 88, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(255, 68, 88, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = 'rgba(255, 68, 88, 0.2)';
                    }}
                    >
                      {post.hero_image && (
                        <div style={{
                          height: '180px',
                          background: `url(http://localhost/cms/assets/${post.hero_image}) center/cover`,
                        }} />
                      )}
                      <div style={{ padding: '1.5rem' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <FaHeart style={{ color: '#ff4458', fontSize: '14px' }} />
                          <span style={{ fontSize: '12px', opacity: 0.7 }}>
                            Liked on {new Date(item.date_created).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="cs_fs_18" style={{ marginBottom: '0.5rem' }}>
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p style={{
                            fontSize: '14px',
                            opacity: 0.7,
                            marginTop: '0.5rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {post.excerpt}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div className="animate-fade-in-up stagger-3" style={{
          marginTop: '3rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
        }}>
          <div className="animate-scale-in stagger-2" style={{
            backgroundColor: 'rgba(79, 71, 71, 0.1)',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div className="cs_fs_48" style={{ fontWeight: 'bold', color: '#5625d8' }}>
              {userPosts.length}
            </div>
            <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              Total Articles
            </div>
          </div>
          <div className="animate-scale-in stagger-3" style={{
            backgroundColor: 'rgba(79, 71, 71, 0.1)',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div className="cs_fs_48" style={{ fontWeight: 'bold', color: '#28a745' }}>
              {userPosts.filter(p => p.status === 'published').length}
            </div>
            <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              Published
            </div>
          </div>
          <div className="animate-scale-in stagger-4" style={{
            backgroundColor: 'rgba(79, 71, 71, 0.1)',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div className="cs_fs_48" style={{ fontWeight: 'bold', color: '#ffc107' }}>
              {userPosts.filter(p => p.status !== 'published').length}
            </div>
            <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              Drafts
            </div>
          </div>
          <div className="animate-scale-in stagger-4" style={{
            backgroundColor: 'rgba(79, 71, 71, 0.1)',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
            border: '2px solid rgba(255, 68, 88, 0.2)',
          }}>
            <div className="cs_fs_48" style={{ fontWeight: 'bold', color: '#ff4458' }}>
              {likedPosts.length}
            </div>
            <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              <FaHeart style={{ fontSize: '14px', marginRight: '0.25rem', verticalAlign: 'middle' }} />
              Liked Posts
            </div>
          </div>
        </div>
      </div>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <Spacing lg="75" md="60" />
    </>
  );
}
